// Zustand store para la pantalla admin de reportes (Sprint 6 T9).
// Mantiene el estado de los 3 reportes + filtros compartidos (sede_id,
// fecha_inicio, fecha_fin) y la pestana activa.
import { create } from 'zustand'
import {
  reportesAdminApi,
  type AlergiaFuenteRow,
  type DecisionJefeRow,
  type ExportKind,
  type SyncOdooRow,
} from '../services/reportesAdmin'

export type ReportesTab = 'decisiones' | 'alergias' | 'sync'

interface Filtros {
  sede_id: number | null
  fecha_inicio: string | null // YYYY-MM-DD
  fecha_fin: string | null
}

interface ReportesState {
  tab: ReportesTab
  filtros: Filtros

  decisiones: DecisionJefeRow[]
  alergias: AlergiaFuenteRow[]
  sync: SyncOdooRow[]

  loading: boolean
  error: string | null

  setTab: (tab: ReportesTab) => void
  setFiltro: <K extends keyof Filtros>(k: K, v: Filtros[K]) => void

  refrescarTabActiva: () => Promise<void>
  exportar: (kind: ExportKind) => Promise<void>
}

function extractError(err: unknown): string {
  if (err instanceof Error) return err.message
  const maybe = err as { response?: { data?: { detail?: string } } }
  return maybe.response?.data?.detail ?? 'Error al consultar reporte'
}

function dateToIsoStart(d: string | null): string | null {
  // YYYY-MM-DD -> YYYY-MM-DDT00:00:00Z. Mantenemos UTC para alinear con
  // el backend (datetimes timezone-aware).
  return d ? `${d}T00:00:00Z` : null
}

function dateToIsoEnd(d: string | null): string | null {
  return d ? `${d}T23:59:59Z` : null
}

export const useReportesAdminStore = create<ReportesState>((set, get) => ({
  tab: 'decisiones',
  filtros: { sede_id: null, fecha_inicio: null, fecha_fin: null },

  decisiones: [],
  alergias: [],
  sync: [],

  loading: false,
  error: null,

  setTab: (tab) => set({ tab }),
  setFiltro: (k, v) => set({ filtros: { ...get().filtros, [k]: v } }),

  refrescarTabActiva: async () => {
    const { tab, filtros } = get()
    set({ loading: true, error: null })
    const rango = {
      sede_id: filtros.sede_id,
      fecha_inicio: dateToIsoStart(filtros.fecha_inicio),
      fecha_fin: dateToIsoEnd(filtros.fecha_fin),
    }
    try {
      if (tab === 'decisiones') {
        const data = await reportesAdminApi.decisionesJefes(rango)
        set({ decisiones: data, loading: false })
      } else if (tab === 'alergias') {
        const data = await reportesAdminApi.alergiasFuente(rango)
        set({ alergias: data, loading: false })
      } else {
        const data = await reportesAdminApi.syncOdoo(rango)
        set({ sync: data, loading: false })
      }
    } catch (err) {
      set({ loading: false, error: extractError(err) })
    }
  },

  exportar: async (kind) => {
    const { filtros } = get()
    const rango = {
      sede_id: filtros.sede_id,
      fecha_inicio: dateToIsoStart(filtros.fecha_inicio),
      fecha_fin: dateToIsoEnd(filtros.fecha_fin),
    }
    try {
      await reportesAdminApi.descargarExport(kind, rango)
    } catch (err) {
      set({ error: extractError(err) })
    }
  },
}))
